import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { map, tap } from 'rxjs'
import { CallsService, ClientCallUser } from '../../calls.service'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { ToastService } from '../../toast.service'
import { User, UsersService } from '../../users.service'
import { constructNameForUser } from '../../utils'

export type ManageCallUsersDialogData = {
  callId: number
  callName: string
  users: ClientCallUser[]
}

@Component({
  selector: 'app-manage-call-users-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocompleteModule,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatTooltip,
    SpinnerComponent,
    TranslateDirective,
    TranslatePipe,
  ],
  templateUrl: './manage-call-users-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './manage-call-users-dialog.component.scss',
})
export class ManageCallUsersDialogComponent {
  data: ManageCallUsersDialogData = inject<ManageCallUsersDialogData>(MAT_DIALOG_DATA)
  callsService = inject(CallsService)
  usersService = inject(UsersService)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  dialog = inject(MatDialog)

  addCallUserMutation = this.callsService.addCallUserMutation()
  deleteCallUserMutation = this.callsService.deleteCallUserMutation()

  users: ClientCallUser[] = [...this.data.users]

  /** The autocomplete's text, which matches one option exactly once a user has been picked. */
  filter = ''

  private currentUserId = this.usersService.currentUser$.value?.id ?? null
  private organizationUsers: User[] = []

  usersResult$ = this.usersService.getOrganizationUsers().pipe(
    tap((result) => {
      const organizationUsers = result.data ?? null
      if (result.isSuccess && organizationUsers !== null) {
        this.organizationUsers = organizationUsers
      }
    })
  )

  /** Null while the reader is still typing, so the button only enables once an option is picked. */
  get selectedUser(): User | null {
    return this.addableUsers.find((user) => this.describeUser(user) === this.filter) ?? null
  }

  // The acting administrator is dropped from the options: granting themselves access is refused by
  // the backend, so offering it would only produce an error.
  get addableUsers(): User[] {
    const filter = this.filter.toLowerCase()
    return this.organizationUsers
      .filter((user) => user.id !== this.currentUserId && !this.users.some((callUser) => callUser.userId === user.id))
      .filter((user) => filter === '' || this.describeUser(user).toLowerCase().includes(filter))
  }

  // Doubles as the option's value, so that the picked option is recognisable from the text alone.
  describeUser(user: User): string {
    return `${constructNameForUser(user)} (${user.email})`
  }

  async addUser() {
    const user = this.selectedUser
    if (user === null) {
      return
    }
    await this.addCallUserMutation.mutateAsync({ callId: this.data.callId, userId: user.id })
    this.users = [...this.users, { userId: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email, isSetToJoin: false }]
    this.filter = ''
    this.toastService.show('CALLS.ADMIN.ADD_CALL_USER.SUCCESS')
  }

  removeUser(callUser: ClientCallUser) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.ADMIN.REVOKE_USER_ACCESS.TITLE'),
        description: this.translateService.instant('CALLS.ADMIN.REVOKE_USER_ACCESS.DESCRIPTION', {
          userName: `${callUser.firstName} ${callUser.lastName}`,
        }),
      },
    })
    dialogRef
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true))
      .subscribe(async (confirmed) => {
        if (confirmed) {
          await this.deleteCallUserMutation.mutateAsync({ callId: this.data.callId, userId: callUser.userId })
          this.users = this.users.filter((user) => user.userId !== callUser.userId)
          this.toastService.show('CALLS.ADMIN.REVOKE_USER_ACCESS.SUCCESS')
        }
      })
  }
}
